/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import OrganizationGroupForm from '../components/applications/OrganizationGroupForm';
import ProgressBarDialog from '../components/common/ProgressBar';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});


function GroupNode({ group, allGroups, types, onEdit, onDelete, onAddChild, onOwnership, collapsedIds, onToggleCollapse, depth = 0, isLast = false }) {
    const { theme } = useTheme();
    // Derived state from props
    const expanded = !collapsedIds.has(group.id);

    const children = allGroups.filter(g => g.parent === group.id);
    const typeName = types.find(t => t.id === group.group_type)?.name || 'Unknown';

    return (
        <div className="d-flex flex-column" style={{ position: 'relative' }}>
            {/* The Node Content */}
            <div className="d-flex align-items-center mb-2" style={{ position: 'relative' }}>
                {/* Connector Line (Horizontal) for non-roots */}
                {depth > 0 && (
                    <div
                        style={{
                            width: 24,
                            height: 1,
                            backgroundColor: '#dee2e6',
                            marginRight: 8
                        }}
                    ></div>
                )}

                {/* Expand Toggle or Dot */}
                <div style={{ width: 24, display: 'flex', justifyContent: 'center' }}>
                    {children.length > 0 ? (
                        <button
                            className="btn btn-sm p-0 d-flex align-items-center justify-content-center border-0"
                            onClick={() => onToggleCollapse(group.id)}
                            style={{
                                width: 20,
                                height: 20,
                                backgroundColor: expanded ? theme.primary : '#e9ecef',
                                color: expanded ? '#fff' : '#6c757d',
                                borderRadius: 4,
                                transition: 'all 0.2s',
                                zIndex: 2
                            }}
                        >
                            <i className={`bi ${expanded ? 'bi-dash' : 'bi-plus'}`} style={{ fontSize: '1rem' }}></i>
                        </button>
                    ) : (
                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#dee2e6' }}></div>
                    )}
                </div>

                {/* Card */}
                <div
                    className="group-card ms-2 flex-grow-1 border-0 shadow-sm d-flex align-items-center justify-content-between p-3 transition-all"
                    style={{
                        backgroundColor: '#fff',
                        borderRadius: 8,
                        borderLeft: `4px solid ${theme.primary}`,
                        minWidth: 300,
                        maxWidth: 600,
                        cursor: 'default'
                    }}
                >
                    <div className="d-flex align-items-center gap-3">
                        <div className="d-flex flex-column">
                            <span className="fw-bold text-dark fs-6">{group.name}</span>
                            <div className="d-flex gap-1 mt-1">
                                <span className="badge bg-light text-secondary border fw-normal px-2 py-1" style={{ fontSize: '0.75rem' }}>
                                    {typeName}
                                </span>
                                {group.ownership_relation_fields && group.ownership_relation_fields.length > 0 && (
                                    <span
                                        className="badge bg-light text-secondary border fw-normal px-2 py-1"
                                        style={{ fontSize: '0.75rem' }}
                                        title="Ownership relations configured"
                                    >
                                        {group.ownership_relation_fields.length} relation{group.ownership_relation_fields.length === 1 ? '' : 's'}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="action-buttons d-flex gap-1 opacity-25 hover-opacity-100 transition-opacity">
                        <button
                            className="btn btn-light btn-sm text-primary rounded-circle btn-hover-primary transition-all"
                            title="Add Sub-Group"
                            onClick={(e) => { e.stopPropagation(); onAddChild(group); }}
                            style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <i className="bi bi-plus-lg"></i>
                        </button>
                        <button
                            className="btn btn-light btn-sm text-info rounded-circle btn-hover-info transition-all"
                            title="Ownership Relations"
                            onClick={(e) => { e.stopPropagation(); onOwnership(group); }}
                            style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <i className="bi bi-diagram-3" style={{ fontSize: '0.8rem' }}></i>
                        </button>
                        <button
                            className="btn btn-light btn-sm text-dark rounded-circle btn-hover-dark transition-all"
                            title="Edit"
                            onClick={(e) => { e.stopPropagation(); onEdit(group); }}
                            style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <i className="bi bi-pencil-fill" style={{ fontSize: '0.8rem' }}></i>
                        </button>
                        <button
                            className="btn btn-light btn-sm text-danger rounded-circle btn-hover-danger transition-all"
                            title="Delete"
                            onClick={(e) => { e.stopPropagation(); onDelete(group); }}
                            style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <i className="bi bi-trash-fill" style={{ fontSize: '0.8rem' }}></i>
                        </button>
                    </div>
                </div>
            </div>

            {/* Children Container using flex padding instead of absolute lines for simplicity & robustness on mobile */}
            {expanded && children.length > 0 && (
                <div className="d-flex flex-column">
                    {children.map((child, index) => (
                        <div key={child.id} className="d-flex">
                            {/* Vertical Line Segment */}
                            <div style={{ width: 24 + 12, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                                {/* 24(toggle width) + 12(half of center approx) - adjustment needed */}
                                {/* Let's just indent manually */}
                                <div style={{
                                    width: 1,
                                    backgroundColor: '#dee2e6',
                                    height: '100%',
                                    marginLeft: 11 // Center under the toggle (width 24/2 - line 1/2)
                                }}></div>
                            </div>

                            <div className="flex-grow-1 pt-2 pb-2">
                                <GroupNode
                                    group={child}
                                    allGroups={allGroups}
                                    types={types}
                                    depth={depth + 1}
                                    onEdit={onEdit}
                                    onDelete={onDelete}
                                    onAddChild={onAddChild}
                                    onOwnership={onOwnership}
                                    collapsedIds={collapsedIds}
                                    onToggleCollapse={onToggleCollapse}
                                    isLast={index === children.length - 1} // Can use this for line termination if we get fancy
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Styles for hover effect since we can't use CSS file */}
            <style>{`
                .group-card:hover {
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
                    transform: translateY(-2px);
                }
                .hover-opacity-100:hover {
                    opacity: 1 !important;
                }
                .group-card:hover .action-buttons {
                    opacity: 1 !important;
                }
                .btn-hover-primary:hover {
                    background-color: ${theme.primary} !important;
                    color: #fff !important;
                }
                .btn-hover-dark:hover {
                    background-color: #343a40 !important;
                    color: #fff !important;
                }
                .btn-hover-info:hover {
                    background-color: #0dcaf0 !important;
                    color: #fff !important;
                }
                .btn-hover-danger:hover {
                    background-color: #dc3545 !important;
                    color: #fff !important;
                }
            `}</style>
        </div>
    );
}

export default function OrganizationGroupsPage() {
    const { theme } = useTheme();
    const { id: applicationId } = useParams();
    const navigate = useNavigate();
    const { showAlertDialog } = useAlertDialog();

    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState([]);
    const [types, setTypes] = useState([]); // Needed for type names
    const [refreshKey, setRefreshKey] = useState(0);

    // Expansion State (Track COLLAPSED IDs, default is expanded)
    const [collapsedIds, setCollapsedIds] = useState(new Set());

    const toggleCollapse = (groupId) => {
        setCollapsedIds(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };

    // Form State
    const [showForm, setShowForm] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [parentForNew, setParentForNew] = useState(null);

    useEffect(() => {
        fetchData();
    }, [applicationId, refreshKey]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [gResp, tResp] = await Promise.all([
                apiHelper.apiCall(`/arcee/groups/?application_id=${applicationId}`),
                apiHelper.apiCall(`/arcee/group-types/?application_id=${applicationId}`)
            ]);

            if (gResp.ok && tResp.ok) {
                const gData = await gResp.json();
                const tData = await tResp.json();
                setGroups(Array.isArray(gData) ? gData : (gData.results || []));
                setTypes(Array.isArray(tData) ? tData : (tData.results || []));
            } else {
                throw new Error("Failed to fetch groups");
            }
        } catch (error) {
            showAlertDialog({ title: 'Error', message: error.message, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateRoot = () => {
        setEditingGroup(null);
        setParentForNew(null);
        setShowForm(true);
    };

    const handleAddChild = (parent) => {
        setEditingGroup(null);
        setParentForNew(parent);
        setShowForm(true);
    };

    const handleOwnership = (group) => {
        navigate(`/dashboard/applications/${applicationId}/groups/${group.id}/ownership-relations`);
    };

    const handleEdit = (group) => {
        setEditingGroup(group);
        // Find existing parent if any, so formatting works for sub-groups
        const parent = groups.find(g => g.id === group.parent);
        setParentForNew(parent || null);
        setShowForm(true);
    };

    // Recursive helper to count descendants
    const countDescendants = (groupId, allGroups) => {
        let count = 0;
        const children = allGroups.filter(g => g.parent === groupId);
        count += children.length;
        children.forEach(child => {
            count += countDescendants(child.id, allGroups);
        });
        return count;
    };

    const handleDelete = (group) => {
        const descendantCount = countDescendants(group.id, groups);
        let message = `Are you sure you want to delete "${group.name}"?`;

        if (descendantCount > 0) {
            message += `\n\nWARNING: This will recursively delete this group AND ${descendantCount} sub-group(s)!`;
        }

        showAlertDialog({
            title: 'Delete Group',
            message: message,
            type: 'warning',
            showCancel: true,
            onOk: async () => {
                try {
                    const resp = await apiHelper.apiCall(`/arcee/groups/${group.id}/delete/`, { method: 'DELETE' });
                    if (!resp.ok) throw new Error("Delete failed");
                    setRefreshKey(k => k + 1);
                    showAlertDialog({ title: 'Success', message: 'Group deleted successfully', type: 'success' });
                } catch (e) {
                    showAlertDialog({ title: 'Error', message: e.message, type: 'error' });
                }
            }
        });
    };

    // Find roots (groups with no parent)
    const rootGroups = groups.filter(g => !g.parent);

    return (
        <div className="d-flex flex-column w-100" style={{ background: "#f8fafc", minHeight: '100vh', overflowX: 'hidden' }}>
            {/* Header */}
            <div className="w-100 px-3 py-3 px-md-4 py-md-4 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                    <button
                        className="btn btn-light rounded-circle shadow-sm"
                        onClick={() => navigate(`/dashboard/applications/${applicationId}`)}
                    >
                        <i className="bi bi-arrow-left"></i>
                    </button>
                    <div>
                        <h4 className="mb-0 fw-bold text-dark">Organization Groups</h4>
                        <small className="text-muted d-none d-md-block">Manage the actual structure of groups (Departments, Teams)</small>
                    </div>
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <button
                        className="btn d-flex align-items-center gap-2 shadow-sm"
                        onClick={() => {
                            const isNested = window.location.pathname.includes('/group-types/');
                            if (isNested) {
                                navigate(`/dashboard/applications/${applicationId}/group-types`);
                            } else {
                                navigate(`/dashboard/applications/${applicationId}/groups/group-types`);
                            }
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.primary;
                            e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff';
                            e.currentTarget.style.color = theme.primary;
                        }}
                        style={{ background: '#fff', border: `1px solid ${theme.primary}`, color: theme.primary, borderRadius: theme.buttonRadius, transition: 'all 0.2s ease-in-out' }}
                    >
                        <i className="bi bi-gear"></i>
                        <span className="d-none d-sm-inline">Configure Types</span>
                    </button>
                    <button
                        className="btn btn-primary d-flex align-items-center gap-2 shadow-sm"
                        onClick={handleCreateRoot}
                        style={{ background: theme.primary, border: 'none', borderRadius: theme.buttonRadius }}
                    >
                        <i className="bi bi-plus-lg"></i>
                        <span className="d-none d-sm-inline">New Root Group</span>
                        <span className="d-sm-none">New</span>
                    </button>
                </div>
            </div>

            <div className="container-fluid p-3 p-md-4" style={{ maxWidth: 1400 }}>
                {loading && groups.length === 0 ? <ProgressBarDialog open={true} text_message="Loading Groups..." /> : (
                    <div className="card border-0 shadow-sm" style={{ borderRadius: theme.cardRadius, minHeight: 400 }}>
                        <div className="card-body p-0 p-md-4">
                            {groups.length === 0 && !loading ? (
                                <div className="text-center text-muted py-5 px-3">
                                    <div className="mb-3 display-4 text-light"><i className="bi bi-diagram-3"></i></div>
                                    <h5>No Groups Defined</h5>
                                    <p className="small text-muted">Start by creating a root group (e.g. "Main Company" or "Headquarters").</p>
                                    <button className="btn btn-primary mt-2" onClick={handleCreateRoot}>Create Root Group</button>
                                </div>
                            ) : (
                                <div className="d-flex flex-column overflow-auto p-3" style={{ maxWidth: '100%' }}>
                                    {/* Spinner Overlay if reloading but data exists? Optional, but cleaner to just show stale data until update */}
                                    {loading && (
                                        <div className="d-flex justify-content-center py-2 text-muted">
                                            <small><i className="bi bi-arrow-repeat spin"></i> Updating...</small>
                                        </div>
                                    )}
                                    <div style={{ minWidth: 'fit-content' }}> {/* Ensure wide trees don't collapse */}
                                        {rootGroups.map(root => (
                                            <div key={root.id} className="mb-5">
                                                <GroupNode
                                                    group={root}
                                                    allGroups={groups}
                                                    types={types}
                                                    onEdit={handleEdit}
                                                    onDelete={handleDelete}
                                                    onAddChild={handleAddChild}
                                                    onOwnership={handleOwnership}
                                                    collapsedIds={collapsedIds}
                                                    onToggleCollapse={toggleCollapse}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {showForm && (
                <OrganizationGroupForm
                    applicationId={applicationId}
                    group={editingGroup}
                    parentGroup={parentForNew}
                    onClose={() => setShowForm(false)}
                    onSuccess={() => setRefreshKey(k => k + 1)}
                />
            )}
        </div>
    );
}
